// How long a quote stands, and whether it still does.
//
// offers.valid_days has been collected since 20260918230823 and shown to the
// trader NOWHERE — not on the dashboard card, not on /requests/:id, not in the
// email. A quote with no stated expiry reads as an open offer, so a trader
// could accept a price a factory considered dead weeks ago, and the first
// anyone hears of it is the supplier withdrawing.
//
// ⚠️ EXPIRY IS DISPLAY ONLY, and must stay that way unless the owner says
// otherwise. Nothing here blocks accepting an expired offer: the database has
// no such rule (offers_accept_only_when_offerable tests the REQUEST, not the
// quote's age), and a client that refused what the server permits would be a
// second authority on the same decision — the exact mistake offers.status was
// kept away from. Tell the trader; let them decide.

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * @returns {{expiresAt: Date, daysLeft: number, expired: boolean}|null}
 *   null when the offer carries no validity — which is most legacy rows, and
 *   renders as no row at all rather than as "unknown".
 */
export function offerValidity(offer, now = new Date()) {
  const days = Number.parseInt(offer?.valid_days, 10);
  if (!Number.isInteger(days) || days <= 0) return null;
  const created = offer?.created_at ? new Date(offer.created_at) : null;
  if (!created || Number.isNaN(created.getTime())) return null;

  const expiresAt = new Date(created.getTime() + days * DAY_MS);
  // Ceil, so "expires later today" reads as 1 day left rather than 0 — a
  // trader told "0 days" reasonably concludes it is already gone.
  const daysLeft = Math.ceil((expiresAt.getTime() - now.getTime()) / DAY_MS);
  return { expiresAt, daysLeft, expired: expiresAt.getTime() <= now.getTime() };
}

/**
 * The trader-facing line. Gregorian calendar with Latin digits even in Arabic:
 * `ar-SA` alone resolves to the Islamic calendar in ICU, which would render a
 * quote's expiry in a calendar the supplier never set it in — the same trap
 * the public RFQ page already documents for its deadline row.
 */
export function offerValidityText(offer, lang = 'en', now = new Date()) {
  const v = offerValidity(offer, now);
  if (!v) return null;
  const locale = lang === 'zh' ? 'zh-CN' : lang === 'ar' ? 'ar-EG-u-ca-gregory-nu-latn' : 'en-GB';
  const date = v.expiresAt.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });

  if (v.expired) {
    return {
      expired: true,
      text: lang === 'ar' ? `انتهت صلاحية هذا العرض في ${date}`
        : lang === 'zh' ? `此报价已于 ${date} 过期`
          : `This quote expired on ${date}`,
    };
  }
  return {
    expired: false,
    text: lang === 'ar' ? `صالح حتى ${date}`
      : lang === 'zh' ? `有效期至 ${date}`
        : `Valid until ${date}`,
  };
}

/**
 * What the supplier answered about the sample — shown only when there IS an
 * answer. The supplier is asked at all only because the trader marked the
 * sample preferred or required, so a null here means the question was never
 * put to them, which is not the same as "no".
 */
export function offerSampleText(offer, lang = 'en', formatMoney) {
  const available = offer?.sample_available;
  if (available !== true && available !== false) return null;

  if (!available) {
    return lang === 'ar' ? 'لا يقدر يوفّر عيّنة'
      : lang === 'zh' ? '无法提供样品'
        : 'Cannot supply a sample';
  }

  const priceRaw = offer?.sample_price;
  const free = priceRaw === null || priceRaw === undefined || priceRaw === '' || Number(priceRaw) === 0;
  const pricePart = free
    ? (lang === 'ar' ? 'مجاناً' : lang === 'zh' ? '免费' : 'free')
    : (typeof formatMoney === 'function' ? formatMoney(priceRaw) : String(priceRaw));

  const days = Number.parseInt(offer?.sample_days, 10);
  const daysPart = Number.isInteger(days) && days > 0
    ? (lang === 'ar' ? `خلال ${days} يوم` : lang === 'zh' ? `${days} 天内` : `in ${days} days`)
    : '';

  const head = lang === 'ar' ? 'يقدر يوفّر عيّنة'
    : lang === 'zh' ? '可提供样品'
      : 'Can supply a sample';

  return [head, pricePart, daysPart].filter(Boolean).join(' · ');
}
